import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { selectDataClinic, selectDataMatch, selectDataPatient, selectDataUserAuthenticated } from 'connect/selectors';
import { selectOngoingCareNoMatch, selectReasonNoMatch } from 'pages/checkout/connect/legacy-selectors';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';
import Logger from 'utils/Logger';

import { stepDefinitions } from '../steps/stepDefinitions';
import { CheckoutStepDefinition, CheckoutStepParams } from '../steps/types';

const getFirstApplicableStepDefinition = (params: CheckoutStepParams) => {
  const applicableStepDefinitions = getApplicableStepDefinitions(params);

  if (!applicableStepDefinitions.length) {
    Logger.error('No applicable step definitions found', { params });
    return null;
  }

  return applicableStepDefinitions[0]!;
};

const getApplicableStepDefinitions = (params: CheckoutStepParams) =>
  stepDefinitions.filter((step) => step.isApplicable(params));

const findFirstIncompleteStep = (params: CheckoutStepParams) => {
  const applicableStepDefinitions = getApplicableStepDefinitions(params);
  const firstIncompleteStep = applicableStepDefinitions
    .find((step) => !step.isComplete(params));

  if (!firstIncompleteStep) {
    return null;
  }

  return firstIncompleteStep;
};

type GetCurrentStepParams = CheckoutStepParams & {
  pathname: string;
};

type CurrentStepResult = {
  currentStep: CheckoutStepDefinition | null;
  currentStepIndex: number;
  applicableStepDefinitions: CheckoutStepDefinition[];
};

const createCurrentStepResult = (
  step: CheckoutStepDefinition | null,
  params: GetCurrentStepParams,
): CurrentStepResult => {
  const applicableStepDefinitions = getApplicableStepDefinitions(params);
  const stepIndex = step ? applicableStepDefinitions.indexOf(step) : -1;

  return {
    currentStep: step,
    currentStepIndex: stepIndex,
    applicableStepDefinitions,
  };
};

const getCurrentStep = (params: GetCurrentStepParams): CurrentStepResult => {
  const defaultCheckoutStep = getFirstApplicableStepDefinition(params);

  const { pathname } = params;
  if (!pathname.includes('checkout')) {
    return createCurrentStepResult(defaultCheckoutStep, params);
  }

  const applicableStepDefinitions = getApplicableStepDefinitions(params);
  const defaultResult = createCurrentStepResult(defaultCheckoutStep, params);

  // URL structure is /results/:clinicId/checkout/:step/:substep
  const currentStepPath = pathname.split('/').at(4);
  const currentStepFromPath = stepDefinitions
    .find((step) => step.path === currentStepPath);

  // If the current step is not found based on the URL, return the default result
  if (!currentStepFromPath) {
    // Only log an error if we have a step path
    if (currentStepPath) {
      Logger.error('Current step not found', { pathname });
    }
    return defaultResult;
  }

  const firstIncompleteStep = findFirstIncompleteStep(params);

  // Check if the current step is in the list of applicable steps
  const isCurrentStepApplicable = applicableStepDefinitions.includes(currentStepFromPath);

  // Check if the current step is past the first incomplete step
  // E.g. the user hasn't completed login but the URL is for the confirm step
  const currentStepIndex = applicableStepDefinitions.indexOf(currentStepFromPath);
  const firstIncompleteStepIndex = firstIncompleteStep ? applicableStepDefinitions.indexOf(firstIncompleteStep) : -1;
  const isCurrentStepPastFirstIncompleteStep = firstIncompleteStep && firstIncompleteStepIndex < currentStepIndex;

  // If the current step is not applicable, or if it's past the first incomplete step,
  // then use the first incomplete step if it exists
  const isFirstCompleteStepPreffered = !isCurrentStepApplicable || isCurrentStepPastFirstIncompleteStep;
  if (isFirstCompleteStepPreffered && firstIncompleteStep) {
    return createCurrentStepResult(firstIncompleteStep, params);
  }

  return createCurrentStepResult(currentStepFromPath, params);
};

export const useCurrentCheckoutStep = () => {
  const { pathname } = useLocation();
  const patient = useSelector(selectDataPatient);
  const isUserAuthenticated = useSelector(selectDataUserAuthenticated);
  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const match = useSelector(selectDataMatch);
  const clinic = useSelector(selectDataClinic);

  const checkoutOngoingCareAnswer = useSelector(selectOngoingCareNoMatch);
  const checkoutSelectedTreatments = useSelector(selectReasonNoMatch);

  return getCurrentStep({
    pathname,
    checkoutState,
    patient,
    isUserAuthenticated,
    match,
    clinic,
    checkoutOngoingCareAnswer,
    checkoutSelectedTreatments,
  });
};

const getNextStepDefinition = (currentStepIndex: number, applicableStepDefinitions: CheckoutStepDefinition[]) => {
  const nextStepDefinition = applicableStepDefinitions[currentStepIndex + 1];
  if (!nextStepDefinition) {
    Logger.error('Next step definition not found', { currentStep: applicableStepDefinitions[currentStepIndex]?.name });
    return applicableStepDefinitions[0]!;
  }

  return nextStepDefinition;
};

export const useNextCheckoutStep = () => {
  const history = useHistory();

  const { clinicId } = useParams<{ clinicId: string; }>();
  const { currentStepIndex, applicableStepDefinitions } = useCurrentCheckoutStep();

  const baseUrl = useMemo(() => `/results/${clinicId}/checkout`, [clinicId]);

  const nextStepUrl = useMemo(() => {
    const nextStepDefinition = getNextStepDefinition(currentStepIndex, applicableStepDefinitions);
    return `${baseUrl}/${nextStepDefinition.path}`;
  }, [currentStepIndex, applicableStepDefinitions, baseUrl]);

  const goToNextStep = useCallback(({ replace = false }: { replace?: boolean; } = {}) => {
    if (replace) {
      history.replace(nextStepUrl);
      return;
    }

    history.push(nextStepUrl);
  }, [history, nextStepUrl]);

  return { goToNextStep, nextStepUrl, baseUrl };
};
